import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, ClipboardList, Inbox, LogOut, PawPrint,
  Menu, X, ShieldCheck, AlertTriangle, Users, Stethoscope, Wrench, Briefcase,
  Settings, Calendar,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import useAdminShortcuts from "./useAdminShortcuts";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
  { to: "/admin/marcacoes", label: "Marcações", icon: ClipboardList, testId: "nav-marcacoes" },
  { to: "/admin/agenda", label: "Agenda", icon: CalendarDays, testId: "nav-agenda" },
  { to: "/admin/disponibilidade", label: "Disponibilidade", icon: Calendar, testId: "nav-disponibilidade" },
  { to: "/admin/clientes", label: "Clientes", icon: Users, testId: "nav-clientes" },
  { to: "/admin/medicos", label: "Médicos", icon: Stethoscope, testId: "nav-medicos" },
  { to: "/admin/servicos", label: "Serviços", icon: Briefcase, testId: "nav-servicos" },
  { to: "/admin/mensagens", label: "Mensagens", icon: Inbox, testId: "nav-mensagens" },
  { to: "/admin/utilizadores", label: "Utilizadores", icon: Wrench, testId: "nav-utilizadores", adminOnly: true },
  { to: "/admin/definicoes", label: "Definições", icon: Settings, testId: "nav-definicoes" },
];

function ClinicSwitcher({ value, onChange }) {
  const opts = [
    { v: "", l: "Todas" },
    { v: "ribeira-brava", l: "Ribeira Brava" },
    { v: "calheta", l: "Calheta" },
  ];
  return (
    <div className="flex flex-wrap gap-1.5 mb-4" data-testid="clinic-switcher">
      {opts.map((o) => {
        const sel = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            data-testid={`clinic-filter-${o.v || "todas"}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
              sel ? "bg-[#1E5F6E] text-white" : "bg-white text-[#5A5A5A] border border-[#1E5F6E]/15 hover:border-[#1E5F6E]/40"
            }`}
          >{o.l}</button>
        );
      })}
    </div>
  );
}

export function ClinicaFilterContext({ value, onChange, children }) {
  return (
    <div>
      <ClinicSwitcher value={value} onChange={onChange} />
      {children}
    </div>
  );
}

export default function AdminShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useAdminShortcuts();

  const visibleNav = NAV.filter((n) => !n.adminOnly || user?.role === "admin");

  const onLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const Sidebar = (
    <aside className="flex h-full w-72 flex-col bg-[#0F3A44] text-[#FAF7F2]" data-testid="admin-sidebar">
      <Link to="/admin" className="flex items-center gap-2 px-5 py-5 border-b border-white/10">
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#E8A87C] text-[#0F3A44]">
          <PawPrint className="h-5 w-5" strokeWidth={2.4} />
        </span>
        <div>
          <div className="font-display text-lg font-extrabold tracking-tight">Vet Clinic</div>
          <div className="text-[10px] uppercase tracking-widest text-white/60">Backoffice</div>
        </div>
      </Link>

      <div className="px-5 py-4 border-b border-white/10">
        <div className="text-xs text-white/60">Sessão</div>
        <div className="font-semibold mt-0.5 truncate" data-testid="session-user">{user?.nome || user?.email}</div>
        <div className="text-xs text-white/60 mt-0.5 inline-flex items-center gap-1.5">
          <ShieldCheck className="h-3 w-3" />
          {user?.role === "admin" ? "Administrador" : "Rececionista"}
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {NAV.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-[#E8A87C] text-[#0F3A44]"
                    : "text-white/75 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-3 py-4 border-t border-white/10">
        <button
          onClick={onLogout}
          data-testid="btn-logout"
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 hover:bg-white/15 text-white px-3.5 py-2.5 text-sm font-bold transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Terminar sessão
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1A1A1A]" data-testid="admin-shell">
      {/* Mobile bar */}
      <header className="lg:hidden sticky top-0 z-40 bg-[#0F3A44] text-white flex items-center justify-between px-4 h-14 border-b border-white/10">
        <Link to="/admin" className="flex items-center gap-2">
          <PawPrint className="h-5 w-5 text-[#E8A87C]" />
          <span className="font-display font-extrabold">Vet Clinic</span>
        </Link>
        <button onClick={() => setOpen(true)} className="h-9 w-9 grid place-items-center rounded-lg bg-white/10" data-testid="open-sidebar">
          <Menu className="h-5 w-5" />
        </button>
      </header>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setOpen(false)}>
          <div className="absolute left-0 top-0 bottom-0" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute top-3 right-3 z-10 h-8 w-8 grid place-items-center rounded-lg bg-white/10 text-white">
              <X className="h-4 w-4" />
            </button>
            {Sidebar}
          </div>
        </div>
      )}

      <div className="lg:flex">
        <div className="hidden lg:block sticky top-0 h-screen">{Sidebar}</div>
        <main className="flex-1 min-w-0 px-5 sm:px-8 lg:px-10 py-8 lg:py-10 max-w-[1400px]">
          {user?.must_change_password && (
            <div className="mb-6 flex items-start gap-3 rounded-2xl bg-[#E8A87C]/15 border border-[#E8A87C]/40 px-4 py-3 text-sm text-[#7a3e1a]" data-testid="must-change-pw">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <strong>Está a usar a password inicial.</strong> Altere-a em{" "}
                <Link to="/admin/definicoes" className="font-bold underline underline-offset-4">Definições</Link>.
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
